import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CommonState, error, isFetching } from '@pec/aion-ui-core/slices/common';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { IOrganizationInfo } from '@pec/aion-ui-core/interfaces/organizationInfo';
import { IProductsResponse } from 'surveyjs/common/types/products';
import { DiscountCodeErrorKey, IDiscount } from '../surveyjs/common/components/questionTypes/MuiDiscount';

const name = 'thirdPartyProducts';

type FetchParams = {
  organizationInfo: IOrganizationInfo;
  countryISO2: string;
  afterEvery?: () => void;
  productName?: string;
  newEmployeeCount?: number;
  chasAddon?: boolean;
};

export const fetchThirdPartyProducts = createAsyncThunk(
  `${name}/fetch`,
  async (
    { organizationInfo, countryISO2, afterEvery, productName, newEmployeeCount, chasAddon }: FetchParams,
    { dispatch }
  ) => {
    const { applicationType, assessmentType, companyType, employeeCount, countryIso2Code } = organizationInfo;
    const productNameParam = productName ? `&ProductName=${productName}` : '';
    const numOfEmployees = newEmployeeCount ? newEmployeeCount : employeeCount.employeeCount;
    const operationType = chasAddon ? 'Addon' : 'ThirdPartyAddon';

    try {
      const { data } = await axios.get<IProductsResponse>(
        `/api/v1/products?Location=${countryISO2}&CustomerType=${companyType}&ApplicationType=${applicationType}&NumOfEmployees=${numOfEmployees}&Country=${countryIso2Code}&AssessmentType=${assessmentType}&OperationType=${operationType}${productNameParam}`
      );
      return data;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    } finally {
      afterEvery && afterEvery();
    }
  }
);

type ValidateProductDiscountCodeParams = {
  request: {
    productId: string;
    location: string;
    subscriptionTier: string;
    code: string;
    applicationType?: string | null;
    assessmentType?: string | null;
  };
  afterSuccess: (data: IDiscount) => void;
  afterError: (error: DiscountCodeErrorKey) => void;
};
export const validateProductDiscountCode = createAsyncThunk(
  `${name}/validateDiscountCode`,
  async (
    { request: { productId, ...others }, afterSuccess, afterError }: ValidateProductDiscountCodeParams,
    { dispatch }
  ) => {
    try {
      const { data } = await axios.post<IDiscount>(`/api/v1/products/validateDiscountCode`, {
        operationType: 'Addon',
        selectedProducts: [productId],
        ...others
      });
      afterSuccess(data);
    } catch (error) {
      if (error.response.status === 406) {
        afterError(error.response.data);
        return;
      }
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export type State = CommonState & {
  thirdPartyProducts: IProductsResponse;
};

const initialState: State = {
  thirdPartyProducts: {} as IProductsResponse,
  isFetching: false,
  error: null
};

const thirdPartyProducts = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchThirdPartyProducts.pending, isFetching)
      .addCase(fetchThirdPartyProducts.fulfilled, (state, action) => {
        state.thirdPartyProducts = action.payload;
        state.isFetching = false;
      })
      .addCase(fetchThirdPartyProducts.rejected, error);
  }
});

export const { reducer: thirdPartyProductsReducer } = thirdPartyProducts;
